import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link as ReactRouterLink } from 'react-router-dom';
import { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Spinner, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import _isEmpty from 'lodash/isEmpty';

import { useFetchCertificationsQuery, useFetchMeQuery } from 'app/apis/reviews';
import { formatPrice } from 'app/helpers/format-helper';
import { getSubmissionPriceDisplay } from 'app/helpers/submission-helper';
import { LanguageTag } from 'app/common/components/language-tag/language-tag';
import { ColorTag } from 'app/common/components/color-tag/color-tag';
import { getSubmissionsPeerVotes, getSubmissionsStudentFeedbacks } from 'app/services/reviews-api-service';
import plagiarismCaseQueueActions from 'app/queue/domains/plagiarism-case-queue/plagiarism-case-queue-actions';
import {
  selectAssignedPlagiarismCases,
  selectCompletedPlagiarismCases,
  selectTotalCompletedPlagiarismCases,
  selectFetchCompletedPlagiarismCasesAsync,
} from 'app/queue/domains/plagiarism-case-queue/plagiarism-case-queue-selectors';
import { getCompletedCertifications } from 'app/queue/domains/certifications/certifications-helpers';
import questionQueueActions from 'app/queue/domains/question-queue/question-queue-actions';
import {
  selectQuestionQueueAssignments,
  selectQuestionQueueHistory,
  selectQuestionQueueHistoryAsync,
} from 'app/queue/domains/question-queue/question-queue-selectors';
import { QuestionQueueActionType } from 'app/queue/domains/question-queue/question-queue-types';
import constants from 'app/queue/views/history/common/constants';
import { fetchAllAssigned, fetchCompleted, clearAllCompleted } from 'app/queue/domains/submissions/submissions-actions';
import {
  getCompletedSubmissions,
  getAssignedSubmissions,
  getSubmissionsAsync,
} from 'app/queue/domains/submissions/submissions-selectors';
import List from 'app/queue/components/list';
import { AssignedPlagiarismCaseListItem } from 'app/queue/components/assigned-plagiarism-case-list-item';
import { AssignedQuestionListItem } from 'app/queue/components/assigned-question-list-item';
import { AssignedSubmissionListItem } from 'app/queue/components/assigned-submission-list-item';
import type { RootState } from 'app/store';
import { useAppDispatch } from 'app/store';
import { TaskHistory } from 'app/queue/views/history/task-history';
import { FeedbackModal } from 'app/queue/views/history/task-history/feedback-modal';
import styles from 'app/queue/views/history/history.module.scss';

import CONFIG from 'configLoader';

const { PAGE_SIZE, HISTORY_DATE_FORMAT } = constants;

type FeedbackRecord = { submission_id: number };

export const getReviewsPageParams = (page = 1) => {
  return { page, per_page: PAGE_SIZE };
};

export const getQuestionPageParams = (page = 1) => {
  return {
    start: (page - 1) * PAGE_SIZE,
    end: page * PAGE_SIZE,
  };
};

const getSubmissionResult = (result: string | null | undefined, t: TFunction) => {
  if (result === 'passed') return t('projectReviews.result.passed');
  if (result === 'failed') return t('projectReviews.result.failed');
  if (result === 'ungradeable') return t('projectReviews.result.ungradeable');
  return '';
};

const makeFeedbackObj = (
  id: number,
  t: TFunction,
  peerFeedbacks: FeedbackRecord[],
  studentFeedbacks: FeedbackRecord[]
) => {
  const submissionPeerFeedbacks = peerFeedbacks.filter((f) => f.submission_id === id);
  const studentFeedback = studentFeedbacks.find((f) => f.submission_id === id) || {};
  const hasFeedback = submissionPeerFeedbacks.length > 0 || !_isEmpty(studentFeedback);

  return hasFeedback
    ? {
        data: t('queue.history.seeFeedback'),
        previewComponent: FeedbackModal,
        previewProps: {
          peerFeedbacks: submissionPeerFeedbacks,
          studentFeedback,
        },
      }
    : { data: '-' };
};

export const History = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: certifications } = useFetchCertificationsQuery();
  const completedPlagiarismCaseCertifications = getCompletedCertifications(certifications) ?? [];

  const completedSubmissions = useSelector(getCompletedSubmissions);
  const assignedSubmissions = useSelector(getAssignedSubmissions);
  const completedAsync = useSelector((state: RootState) => getSubmissionsAsync(state, 'fetchCompleted'));
  const assignedAsync = useSelector((state: RootState) => getSubmissionsAsync(state, 'fetchAllAssigned'));
  const assignedQuestions = useSelector(selectQuestionQueueAssignments);
  const questionHistory = useSelector(selectQuestionQueueHistory);
  const questionHistoryAsync = useSelector(selectQuestionQueueHistoryAsync);
  const assignedPlagiarismCases = useSelector(selectAssignedPlagiarismCases);
  const completedPlagiarismCases = useSelector(selectCompletedPlagiarismCases);
  const totalCompletedPlagiarismCases = useSelector(selectTotalCompletedPlagiarismCases);
  const fetchCompletedPlagiarismCasesAsync = useSelector(selectFetchCompletedPlagiarismCasesAsync);
  const { data: reviewsMe } = useFetchMeQuery();

  const [submissionPage, setSubmissionPage] = useState(1);
  const [questionPage, setQuestionPage] = useState(1);
  const [plagiarismCasePage, setPlagiarismCasePage] = useState(1);
  const [peerFeedbacks, setPeerFeedbacks] = useState<FeedbackRecord[]>([]);
  const [studentFeedbacks, setStudentFeedbacks] = useState<FeedbackRecord[]>([]);

  useEffect(() => {
    dispatch(fetchAllAssigned());

    dispatch(clearAllCompleted());
    dispatch(fetchCompleted(getReviewsPageParams(1)));

    dispatch(questionQueueActions.clearQueueHistory());
    dispatch(questionQueueActions.fetchQueueHistory(PAGE_SIZE));

    dispatch(plagiarismCaseQueueActions.clearCompletedPlagiarismCases());
    dispatch(plagiarismCaseQueueActions.fetchCompletedPlagiarismCases(getReviewsPageParams(1)));
  }, [dispatch]);

  useEffect(() => {
    if (completedSubmissions.length === 0) {
      return;
    }

    const ids = completedSubmissions.map((s) => s.id);
    getSubmissionsPeerVotes(ids).then(setPeerFeedbacks);
    getSubmissionsStudentFeedbacks(ids).then(setStudentFeedbacks);
  }, [completedSubmissions]);

  const fetchSubmissionPage = (page: number) => {
    setSubmissionPage(page);
    dispatch(fetchCompleted(getReviewsPageParams(page)));
  };

  const loadQuestionPage = (page: number) => {
    const { start, end } = getQuestionPageParams(page);
    if (questionHistory.totalCount != null && start > questionHistory.totalCount) {
      return;
    }

    setQuestionPage(page);
    if (
      end > questionHistory.actions.length &&
      questionHistory.totalCount != null &&
      questionHistory.totalCount > questionHistory.actions.length
    ) {
      const lastAction = questionHistory.actions[questionHistory.actions.length - 1];
      const afterCursor = lastAction ? lastAction.cursor : undefined;
      dispatch(questionQueueActions.fetchQueueHistory(PAGE_SIZE, afterCursor));
    }
  };

  const loadPlagiarismCasePage = (page: number) => {
    const start = (page - 1) * PAGE_SIZE;
    if (start > (totalCompletedPlagiarismCases || 0)) {
      return;
    }
    setPlagiarismCasePage(page);

    const end = start + PAGE_SIZE;
    if (
      end > completedPlagiarismCases.length &&
      (totalCompletedPlagiarismCases || 0) > completedPlagiarismCases.length
    ) {
      dispatch(plagiarismCaseQueueActions.fetchCompletedPlagiarismCases(getReviewsPageParams(page)));
    }
  };

  const endSubmissions = Math.min(submissionPage * PAGE_SIZE, completedSubmissions.length);
  const submissionLastPageSize = endSubmissions % PAGE_SIZE || PAGE_SIZE;
  const startSubmissions = Math.max(endSubmissions - submissionLastPageSize, 0);
  const submissionTableData = completedSubmissions.slice(startSubmissions, endSubmissions).map((submission) => {
    const completed = dayjs(submission.completed_at).format(HISTORY_DATE_FORMAT);
    const url = `/project-reviews/${submission.id}/feedback`;
    const isCareer = submission.project?.is_career;
    const { base, bonus, total } = getSubmissionPriceDisplay(submission);

    return {
      [t('table.columns.projectName')]: { data: submission.project.name },
      [t('table.columns.language')]: {
        data: <LanguageTag languageCode={submission.language ?? ''} />,
      },
      [t('table.columns.completed')]: { data: completed },
      [t('table.columns.base')]: { data: base ? <ColorTag variant="green">{base}</ColorTag> : '-' },
      [t('table.columns.bonus')]: { data: bonus ? <ColorTag variant="purple">{bonus}</ColorTag> : '-' },
      [t('table.columns.result')]: {
        data: getSubmissionResult(submission.result, t),
      },
      [t('table.columns.feedback')]: makeFeedbackObj(submission.id, t, peerFeedbacks, studentFeedbacks),
      [t('table.columns.total')]: { data: <ColorTag variant="green-dark">{total}</ColorTag> },
      [t('table.columns.link')]: {
        data: isCareer ? (
          <Text>{t('queue.history.confidential')}</Text>
        ) : (
          <Link href={url} isExternal>
            {t('queue.history.viewReview')}
          </Link>
        ),
      },
    };
  });

  let { start: questionStart, end: questionEnd } = getQuestionPageParams(questionPage);
  questionEnd = Math.min(questionEnd, questionHistory.actions.length);
  const questionLastPageSize = questionEnd % PAGE_SIZE || PAGE_SIZE;
  questionStart = Math.max(questionEnd - questionLastPageSize, 0);
  const questionHistoryTableData = questionHistory.actions.slice(questionStart, questionEnd).map((action) => {
    const completed = dayjs(action.createdAt).format(HISTORY_DATE_FORMAT);
    const price = formatPrice(action.payment.amount);
    const type = action.type === QuestionQueueActionType.COMMENT ? 'Comment' : 'Answer';
    const url = `${CONFIG.knowledge_url}/questions/${action.questionID}`;

    return {
      [t('table.columns.projectName')]: { data: action.projectTitle },
      [t('table.columns.earned')]: { data: price },
      [t('table.columns.completed')]: { data: completed },
      [t('table.columns.type')]: { data: type },
      [t('table.columns.link')]: {
        data: (
          <Link href={url} isExternal>
            {t('queue.history.viewQuestion')}
          </Link>
        ),
      },
    };
  });

  const endPlagiarism = Math.min(plagiarismCasePage * PAGE_SIZE, completedPlagiarismCases.length);
  const plagiarismLastPageSize = endPlagiarism % PAGE_SIZE || PAGE_SIZE;
  const startPlagiarism = Math.max(endPlagiarism - plagiarismLastPageSize, 0);
  const completedPlagiarismCaseTableData = completedPlagiarismCases
    .slice(startPlagiarism, endPlagiarism)
    .map((plagiarismCase) => {
      const completedAt = dayjs(plagiarismCase.completedAt).format(HISTORY_DATE_FORMAT);
      const price = formatPrice(plagiarismCase.price);
      const resultWithSpaces = plagiarismCase.result.replace('_', ' ');
      const formattedResult = resultWithSpaces.charAt(0).toUpperCase() + resultWithSpaces.substring(1);

      return {
        [t('table.columns.projectName')]: { data: plagiarismCase.project.name },
        [t('table.columns.earned')]: { data: price },
        [t('table.columns.completed')]: { data: completedAt },
        [t('table.columns.result')]: { data: formattedResult },
      };
    });

  const isAllLoading = completedAsync.firstLoad && assignedAsync.firstLoad;
  const isCompletedLoading = completedAsync.loading;
  const isAssignedLoading = assignedAsync.loading;
  const isQuestionHistoryLoading = questionHistoryAsync.loading;
  const hasActiveTasks =
    assignedSubmissions.length > 0 || assignedQuestions.length > 0 || assignedPlagiarismCases.length > 0;

  return (
    <div>
      <div className={styles['title-bar']}>
        <h1>{t('queue.history.history')}</h1>
      </div>

      {isAllLoading ? (
        <div className={styles['loader']}>
          <div>
            <Spinner />
          </div>
        </div>
      ) : (
        <div className={styles.content}>
          <section>
            <h3>{t('common.active')}</h3>
            {hasActiveTasks && (
              <List>
                {assignedSubmissions.map((sub) => {
                  return <AssignedSubmissionListItem key={sub.id} submission={sub} />;
                })}
                {assignedPlagiarismCases.map((plagiarismCase) => {
                  return <AssignedPlagiarismCaseListItem key={plagiarismCase.id} plagiarismCase={plagiarismCase} />;
                })}
                {assignedQuestions.map((assignment) => {
                  return <AssignedQuestionListItem key={`question-${assignment.questionID}`} assignment={assignment} />;
                })}
              </List>
            )}
            {!hasActiveTasks && !isAssignedLoading && (
              <label className={`${styles['empty']} label`}>{t('queue.history.noActiveTasks')}</label>
            )}
            {!hasActiveTasks && isAssignedLoading && (
              <div className={styles['loader']}>
                <Spinner />
              </div>
            )}
          </section>

          <TaskHistory
            title={t('queue.history.reviewsHistory')}
            isLoading={isCompletedLoading}
            tableData={submissionTableData}
            page={submissionPage}
            onNextPage={() => fetchSubmissionPage(submissionPage + 1)}
            onPreviousPage={() => fetchSubmissionPage(submissionPage - 1)}
          />

          {completedPlagiarismCaseCertifications.length > 0 && reviewsMe?.can_join_plagiarism_queue && (
            <TaskHistory
              title={t('queue.history.reviewsPlagiarismCaseHistory')}
              isLoading={fetchCompletedPlagiarismCasesAsync.loading}
              tableData={completedPlagiarismCaseTableData}
              page={plagiarismCasePage}
              onNextPage={() => loadPlagiarismCasePage(plagiarismCasePage + 1)}
              onPreviousPage={() => loadPlagiarismCasePage(plagiarismCasePage - 1)}
            />
          )}

          <TaskHistory
            title={t('queue.history.questionHistory')}
            isLoading={isQuestionHistoryLoading}
            tableData={questionHistoryTableData}
            page={questionPage}
            onNextPage={() => loadQuestionPage(questionPage + 1)}
            onPreviousPage={() => loadQuestionPage(questionPage - 1)}
          />

          {questionHistoryTableData.length > 0 && (
            <Text fontSize="xs" color="gray.600">
              <Trans
                i18nKey="queue.history.questionHistoryIncludes"
                components={{
                  handbook_link: <Link as={ReactRouterLink} to="/guidelines/knowledge" isExternal fontSize="xs" />,
                }}
              />
            </Text>
          )}
        </div>
      )}
    </div>
  );
};

// TODO(GURU-3567): Consolidate into only one queue.
// - Refactor submissions state to persist history rather than just current
//   page.
// - Interleave results of submissions/questions history.
// TODO(GURU-3452): Always show question history when enabled for all review
// certified mentors, even if empty.
export default History;
